
import { useState, useEffect, useRef } from 'react'
import Layout from '../../components/Layout'
import HierarchicalTree from '../../components/HierarchicalTree'
import { eventsData as initialEventsData } from '../../data/eventsData'
import api, { searchByUsername } from '../../services/api'
import { API_ENDPOINTS } from '../../config/api'
import Toast from '../../utils/toast'
import { isUserRole } from '../../utils/roles'
import '../reports/style.css'
import './general-lock.css'


/**
 * GeneralLock Component
 * 
 * Page for general lock settings with search and filter options.
 */
function GeneralLock() {
  const [clientName, setClientName] = useState('')
  const [transactionCode, setTransactionCode] = useState('')
  const [passwordError, setPasswordError] = useState('')

  // Search & Dropdown State
  const [searchResults, setSearchResults] = useState([])
  const [showDropdown, setShowDropdown] = useState(false)
  const searchTimeoutRef = useRef(null)

  // User State
  const [selfProfile, setSelfProfile] = useState(null)
  const [targetProfile, setTargetProfile] = useState(null) // The user we are currently editing

  const [eventsExpandedItems, setEventsExpandedItems] = useState(new Set())

  // Events data structure - imported from separate data file
  const [eventsData, setEventsData] = useState(initialEventsData)

  // Casino data structure
  const [casinoData, setCasinoData] = useState([])

  // Helper to fetch locks for a specific ID
  const fetchLocks = async (staffId, type = 'OTHER') => {
    try {
      const locksResponse = await api.get(`${API_ENDPOINTS.TABLE_CASINO.LOCKS}/${staffId}`, {
        params: { type }
      })
      if (locksResponse.data && locksResponse.data.success) {
        const locks = locksResponse.data.data
        const lockMap = new Map(locks.map(l => [l.table_casino_id, l.is_locked]))

        setCasinoData(prev => prev.map(item => ({
          ...item,
          isLocked: lockMap.get(item.id) || false
        })))
      }
    } catch (error) {
      console.error('Error fetching locks:', error)
    }
  }

  // 1. Fetch Casino List & Self Profile on Mount
  useEffect(() => {
    const initData = async () => {


      try {
        // Fetch Casino List
        const listRes = await api.get(API_ENDPOINTS.TABLE_CASINO.LIST)
        if (listRes.data && listRes.data.success) {
          setCasinoData(listRes.data.data.map(item => ({
            ...item,
            isLocked: false
          })))
        }

        // Fetch Self Profile based on active role
        const activeRole = localStorage.getItem('activeRole');
        let profileRes;
        let profile;

        if (activeRole === 'owner') {
          profileRes = await api.get(API_ENDPOINTS.USER.OWNER_PROFILE);
          if (profileRes.data && profileRes.data.success) {
            profile = profileRes.data.data;
          }
        } else {
          profileRes = await api.get(API_ENDPOINTS.USER.PROFILE);
          if (profileRes.data && profileRes.data.success) {
            profile = profileRes.data.data.staff || profileRes.data.data.user;
          }
        }

        if (profile) {
          // Normalize profile object
          const normalizedProfile = {
            id: profile.owner_id || profile.staff_id || profile.id,
            username: profile.username,
            role: profile.role
          }
          setSelfProfile(normalizedProfile)
          setTargetProfile(normalizedProfile) // Default to self

          // Load locks for self immediately
          console.log('Self Profile Loaded:', normalizedProfile)
          await fetchLocks(normalizedProfile.id, 'SELF')
        }

      } catch (error) {
        console.error('Error initializing data:', error)
      }
    }

    initData()
  }, [])



  // Handle Search Input Change (Debounced)
  const handleSearchChange = (e) => {
    const value = e.target.value
    setClientName(value)

    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current)

    if (!value.trim()) {
      setSearchResults([])
      setShowDropdown(false)
      return
    }

    searchTimeoutRef.current = setTimeout(async () => {
      try {
        const res = await searchByUsername(value)
        if (res.success && res.data && res.data.results) {
          setSearchResults(res.data.results)
          setShowDropdown(true)
        }
      } catch (error) {
        console.error('Search error:', error)
      }
    }, 300)
  }

  // Handle Select User from Dropdown
  const handleSelectUser = async (user) => {
    setClientName(user.username)
    setShowDropdown(false)

    // Normalize selected user
    const selected = {
      id: user.staff_id || user.user_id,
      username: user.username,
      role: user.role || user.user_type
    }

    console.log('DEBUG handleSelectUser:', { user, selected });

    setTargetProfile(selected)
    const type = isUserRole(selected.role) ? 'USER' : 'OTHER';
    console.log('DEBUG handleSelectUser type:', type);
    await fetchLocks(selected.id, type)
  }

  // Handle Reset
  const handleReset = async () => {
    setClientName('')
    setTransactionCode('')
    setSearchResults([])
    setShowDropdown(false)
    setPasswordError('')

    if (selfProfile) {
      setTargetProfile(selfProfile)
      await fetchLocks(selfProfile.id, 'SELF')
    }
  }

  const toggleEventsItem = (itemId) => {
    setEventsExpandedItems(prev => {
      const newSet = new Set(prev)
      if (newSet.has(itemId)) {
        newSet.delete(itemId)
      } else {
        newSet.add(itemId)
      }
      return newSet
    })
  }

  const handleMarketToggle = (marketId) => {
    setEventsData(prev => prev.map(item =>
      item.marketId === marketId
        ? { ...item, enabled: !item.enabled }
        : item
    ))
  }

  const handleSubmarketToggle = (marketId, submarketId) => {
    setEventsData(prev => prev.map(market =>
      market.marketId === marketId
        ? {
          ...market,
          tree: market.tree.map(submarket =>
            submarket.submarketId === submarketId
              ? { ...submarket, enabled: !submarket.enabled }
              : submarket
          )
        }
        : market
    ))
  }

  const handleSubtreeToggle = (marketId, submarketId, subtreeIndex) => {
    setEventsData(prev => prev.map(market =>
      market.marketId === marketId
        ? {
          ...market,
          tree: market.tree.map(submarket =>
            submarket.submarketId === submarketId
              ? {
                ...submarket,
                subtree: submarket.subtree.map((subtreeItem, stIdx) =>
                  stIdx === subtreeIndex
                    ? { ...subtreeItem, enabled: !(subtreeItem.enabled || false) }
                    : subtreeItem
                )
              }
              : submarket
          )
        }
        : market
    ))
  }

  // Toggle events under a match (e.g., Over / Under) – Match row itself has no checkbox
  const handleEventToggle = (marketId, submarketId, subtreeIndex, matchIndex, eventIndex) => {
    setEventsData(prev => prev.map(market =>
      market.marketId === marketId
        ? {
          ...market,
          tree: market.tree.map(submarket =>
            submarket.submarketId === submarketId
              ? {
                ...submarket,
                subtree: submarket.subtree.map((subtreeItem, stIdx) =>
                  stIdx === subtreeIndex && subtreeItem.matches
                    ? {
                      ...subtreeItem,
                      matches: subtreeItem.matches.map((match, mIdx) =>
                        mIdx === matchIndex && match.evnets
                          ? {
                            ...match,
                            evnets: match.evnets.map((eventItem, eIdx) =>
                              eIdx === eventIndex
                                ? { ...eventItem, enabled: !(eventItem.enabled || false) }
                                : eventItem
                            )
                          }
                          : match
                      )
                    }
                    : subtreeItem
                )
              }
              : submarket
          )
        }
        : market
    ))
  }

  // Casino handlers
  const handleCasinoToggle = async (tableId, currentStatus) => {
    if (!targetProfile) {
      Toast.fire({
        icon: 'error',
        title: 'No user selected'
      });
      return
    }

    // Validate transaction password - must be "12345"
    // const correctPassword = '12345'
    // if (transactionCode !== correctPassword) {
    //   setPasswordError('Invalid transaction code. Please enter the correct password.')
    //   return
    // }

    // Clear any previous errors
    setPasswordError('')

    const newStatus = !currentStatus

    // Optimistic update
    setCasinoData(prev => prev.map(item =>
      item.id === tableId ? { ...item, isLocked: newStatus } : item
    ))

    try {
      // Determine type: SELF if target is same as self, else OTHER
      // Fix: Check role equality to prevent ID collision between Owner/Staff and User
      const isSelf = selfProfile && targetProfile.id === selfProfile.id && targetProfile.role === selfProfile.role;
      let type = isSelf ? 'SELF' : 'OTHER';

      console.log('DEBUG handleCasinoToggle:', { targetProfile, isSelf });

      if (isUserRole(targetProfile.role)) type = 'USER';

      const payload = {
        tableCasinoId: tableId,
        isLocked: newStatus,
        type,
        transactionPassword: transactionCode
      };

      // Only send staffId if it's NOT self (for self, backend uses token)
      if (!isSelf) {
        if (type === 'USER') {
          payload.userId = targetProfile.id;
        } else {
          payload.staffId = targetProfile.id;
        }
      }

      await api.post(API_ENDPOINTS.TABLE_CASINO.LOCK, payload)
    } catch (error) {
      console.error('Error updating lock:', error)
      // Revert on error
      setCasinoData(prev => prev.map(item =>
        item.id === tableId ? { ...item, isLocked: currentStatus } : item
      ))
      Toast.fire({
        icon: 'error',
        title: 'Failed to update lock'
      });
    }
  }



  return (
    <Layout title="General Lock - Diamond Admin">
      <div className="listing-grid">
        <div className="account-list-statement">
          {/* Page Title */}
          <div className="row">
            <div className="col-12">
              <div className="page-title-box d-flex align-items-center justify-content-between">
                <h4 className="mb-0 font-size-18">
                  General Lock
                </h4>
              </div>
            </div>
          </div>

          {/* Filter Form */}
          <div className="row">
            <div className="col-12">
              <div className="">
                <div className="">
                  <form method="post" className="ajaxFormSubmit" onSubmit={(e) => e.preventDefault()}>
                    <div className="row row5">
                      <div className="col-md-3 position-relative">
                        <input
                          type="text"
                          className="form-control"
                          placeholder="Search By Client Name"
                          value={clientName}
                          onChange={handleSearchChange}
                          onFocus={() => clientName && setShowDropdown(true)}
                        />
                        {/* Search Dropdown */}
                        {showDropdown && searchResults.length > 0 && (
                          <div className="search-dropdown">
                            {searchResults.map(user => (
                              <div
                                key={user.wallet_id || user.user_id}
                                className="search-dropdown-item"
                                onClick={() => handleSelectUser(user)}
                              >
                                {user.username} ({user.user_type || user.role})
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                      <div className="col-md-3">
                        <input
                          type="text"
                          className={`form-control ${passwordError ? 'is-invalid' : ''}`}
                          placeholder="Transaction Code"
                          value={transactionCode}
                          onChange={(e) => {
                            setTransactionCode(e.target.value)
                            // Clear password error when user types
                            if (passwordError) {
                              setPasswordError('')
                            }
                          }}
                        />
                        {passwordError && (
                          <div className="invalid-feedback d-block">
                            {passwordError}
                          </div>
                        )}
                      </div>
                      <div className="col-md-2 d-flex align-items-end">
                        <button
                          type="button"
                          className="btn btn-secondary"
                          onClick={handleReset}
                        >
                          Reset
                        </button>
                      </div>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </div>

          {/* Content Area */}
          <div className="user-lock-container">
            <div className="row mt-4">

              <div className='col-lg-6 col-12'>
                <h4 className="ptitle">Events</h4>
                <HierarchicalTree
                  data={eventsData}
                  expandedItems={eventsExpandedItems}
                  onToggleItem={toggleEventsItem}
                  onMarketToggle={handleMarketToggle}
                  onSubmarketToggle={handleSubmarketToggle}
                  onSubtreeToggle={handleSubtreeToggle}
                  onEventToggle={handleEventToggle}
                  prefix=""
                />
              </div>

              <div className='col-lg-6 col-12'>
                <h4 className="ptitle">Casino List</h4>
                <div className="casino-list-container" style={{ maxHeight: '600px', overflowY: 'auto', border: '1px solid #eee', padding: '15px' }}>
                  <ul className="list-unstyled">
                    {casinoData.map((game) => (
                      <li key={game.id} className="mb-2">
                        <div className="custom-control custom-checkbox">
                          <input
                            type="checkbox"
                            className="custom-control-input"
                            id={`casino-check-${game.id}`}
                            checked={game.isLocked || false}
                            onChange={() => handleCasinoToggle(game.id, game.isLocked)}
                          />
                          <label className="custom-control-label" htmlFor={`casino-check-${game.id}`}>
                            {game.tablename}
                          </label>
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

            </div>
          </div>
        </div>
      </div>
    </Layout>
  )
}

export default GeneralLock

